import * as React from "react";
import { Grid, IconButton, InputLabel } from "@mui/material";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { TrashIcon } from "@phosphor-icons/react/dist/ssr/Trash";
import { FormProvider, useForm } from "react-hook-form";

import { useCreateField, useField, useUpdateField } from "@/hooks/api/use-fields";
import { ComponentLoader } from "@/components/componentLoader/component-loader";
import { CustomDrawer } from "@/components/custom-drawer";
import { DynamicField } from "@/components/dynamicField/dynamic-field";
import { DynamicFieldType } from "@/components/dynamicField/types";

const fieldTypes = [
	{ value: "text", label: "Text" },
	{ value: "number", label: "Number" },
	{ value: "date", label: "Date" },
	{ value: "boolean", label: "Boolean" },
	{ value: "select", label: "Select" },
];

export function CreateField({
	isCreateFieldDrawerOpen,
	onCreateDrawerClose,
	fieldId,
}: {
	isCreateFieldDrawerOpen: boolean;
	onCreateDrawerClose: () => void;
	fieldId: string | null;
}): React.JSX.Element {
	const isEdit = !!fieldId;

	const { data: field, isLoading: isFieldLoading } = useField(fieldId || "");
	const createFieldMutation = useCreateField();
	const updateFieldMutation = useUpdateField();

	const methods = useForm<any>({
		defaultValues: {
			name: "",
			label: "",
			description: "",
			type: "",
			options: [],
			required: false,
		},
	});

	const {
		handleSubmit,
		reset,
		setError,
		control,
		setValue,
		watch,
		formState: { isSubmitting, errors },
	} = methods;

	const type = watch("type");
	const options = watch("options.options");

	React.useLayoutEffect(() => {
		if (field && isEdit) {
			reset({
				name: field.name,
				label: field.label,
				description: field.description,
				type: field.type,
				options: field.options,
				required: field.required,
			});
		} else {
			reset({
				name: "",
				label: "",
				description: "",
				type: "",
				options: [],
				required: false,
			});
		}
	}, [field, isEdit, reset, isCreateFieldDrawerOpen]);

	const onSubmitForm = async (values: any) => {
		try {
			const formOptions =
				type === "select"
					? {
							options: options.map((option) => ({
								label: option.label,
								value: option.value,
							})),
						}
					: type === "number"
						? {
								min: values?.options?.min,
								max: values?.options?.max,
								step: values?.options?.step,
							}
						: {};

			const formData = {
				name: values.name,
				label: values.label,
				description: values.description,
				type: values.type,
				options: formOptions,
				required: values.required,
			};

			await (isEdit && fieldId
				? updateFieldMutation.mutateAsync({ id: fieldId, data: formData })
				: createFieldMutation.mutateAsync(formData));

			onCreateDrawerClose();
		} catch (error: any) {
			const errorMessages = error?.response?.data?.message;
			if (!Array.isArray(errorMessages)) {
				setError("root", { message: errorMessages });
				return;
			}

			const errorMessage = errorMessages[0];
			setError(errorMessage.split(" ")[0], { message: errorMessage });
		}
	};

	const handleAddOption = () => {
		setValue("options.options", [...(options || []), { label: "", value: "" }]);
	};

	const handleRemoveOption = (index: number) => {
		options.splice(index, 1);
		setValue("options.options", options);
	};

	return (
		<React.Fragment>
			<CustomDrawer
				open={isCreateFieldDrawerOpen}
				onClose={onCreateDrawerClose}
				title={isEdit ? "Edit Field" : "Create Field"}
				onSubmit={handleSubmit(onSubmitForm)}
				isSubmitButtonDisabled={isSubmitting || createFieldMutation.isPending || updateFieldMutation.isPending}
				submitButtonText={isEdit ? "Update Field" : "Create Field"}
      	width="420px"
			>
				<ComponentLoader loading={isFieldLoading} />
				<FormProvider {...methods}>
					<Grid container spacing={2}>
						<Grid size={{ xs: 12, md: 12 }}>
							<DynamicField
								control={control}
								disabled={isEdit}
								name="name"
								type={DynamicFieldType.TEXT}
								label="Name"
								dynamicField={{
									name: "name",
									label: "Name",
									required: true,
									type: DynamicFieldType.TEXT,
								}}
							/>
						</Grid>

						<Grid size={{ xs: 12, md: 12 }}>
							<DynamicField
								control={control}
								name="label"
								type={DynamicFieldType.TEXT}
								label="Label"
								dynamicField={{
									name: "label",
									label: "Label",
									required: true,
									type: DynamicFieldType.TEXT,
								}}
							/>
						</Grid>

						<Grid size={{ xs: 12, md: 12 }}>
							<DynamicField
								control={control}
								name="description"
								type={DynamicFieldType.TEXT_AREA}
								label="Description"
								dynamicField={{
									name: "description",
									label: "Description",
									multiline: true,
									required: true,
									type: DynamicFieldType.TEXT_AREA,
								}}
							/>
						</Grid>

						<Grid size={{ xs: 12, md: 12 }}>
							<DynamicField
								control={control}
								name="type"
								type={DynamicFieldType.SELECT}
								label="Type"
								disabled={isEdit}
								onChange={(newType) => (newType === "select" ? handleAddOption() : setValue("options.options", []))}
								dynamicField={{
									name: "type",
									label: "Type",
									required: true,
									type: DynamicFieldType.SELECT,
									options: fieldTypes,
								}}
							/>
						</Grid>

						{type === "number" && (
							<>
								<Grid size={{ xs: 12, md: 4 }}>
									<DynamicField
										control={control}
										name="options.min"
										type={DynamicFieldType.NUMBER}
										label="Min"
										dynamicField={{
											name: "options.min",
											label: "Min",
											type: DynamicFieldType.NUMBER,
										}}
									/>
								</Grid>
								<Grid size={{ xs: 12, md: 4 }}>
									<DynamicField
										control={control}
										name="options.max"
										type={DynamicFieldType.NUMBER}
										label="Max"
										dynamicField={{
											name: "options.max",
											label: "Max",
											type: DynamicFieldType.NUMBER,
										}}
									/>
								</Grid>
								<Grid size={{ xs: 12, md: 4 }}>
									<DynamicField
										control={control}
										name="options.step"
										type={DynamicFieldType.NUMBER}
										label="Step"
										dynamicField={{
											name: "options.step",
											label: "Step",
											type: DynamicFieldType.NUMBER,
										}}
									/>
								</Grid>
							</>
						)}

						{type === "select" && (
							<>
								<Grid size={{ xs: 12, md: 12 }}>
									<InputLabel sx={{ position: "relative", zIndex: 0 }} error={Boolean(errors.options)} required={true}>
										Options
									</InputLabel>
									<Grid container spacing={2} width="100%" mb={2} mt={options.length > 0 ? 2 : 0}>
										{options.map((option, index) => (
											<Grid
												container
												spacing={2}
												key={index}
												width="100%"
												sx={{ p: 2, border: "1px solid var(--mui-palette-divider)", borderRadius: 1 }}
											>
												<Grid size={{ xs: 12, md: 5.5 }}>
													<DynamicField
														control={control}
														type={DynamicFieldType.TEXT}
														label="Label"
														name={`options.options.${index}.label`}
														dynamicField={{
															required: true,
															name: `options.options.${index}.label`,
															label: "Label",
															value: option.value,
															type: DynamicFieldType.TEXT,
														}}
													/>
												</Grid>
												<Grid size={{ xs: 12, md: 5.5 }}>
													<DynamicField
														control={control}
														type={DynamicFieldType.TEXT}
														label="Value"
														name={`options.options.${index}.value`}
														dynamicField={{
															required: true,
															name: `options.options.${index}.value`,
															label: "Value",
															value: option.value,
															type: DynamicFieldType.TEXT,
														}}
													/>
												</Grid>
												<Grid
													size={{ xs: 12, md: 1 }}
													sx={{ alignSelf: "flex-end", justifyContent: "center", display: "flex" }}
												>
													{options.length > 1 && (
														<IconButton
															color="error"
															onClick={() => handleRemoveOption(index)}
															sx={{ alignSelf: "flex-end", backgroundColor: "grey.100" }}
														>
															<TrashIcon />
														</IconButton>
													)}
												</Grid>
											</Grid>
										))}
									</Grid>
									<IconButton
										color="primary"
										onClick={() => handleAddOption()}
										sx={{ alignSelf: "flex-end", backgroundColor: "grey.100" }}
									>
										<PlusIcon />
									</IconButton>
								</Grid>
							</>
						)}

						<Grid size={{ xs: 12, md: 12 }}>
							<DynamicField
								control={control}
								name="required"
								type={DynamicFieldType.BOOLEAN}
								label="Required"
								dynamicField={{
									name: "required",
									label: "Required",
									type: DynamicFieldType.BOOLEAN,
								}}
							/>
						</Grid>
					</Grid>
				</FormProvider>
			</CustomDrawer>
		</React.Fragment>
	);
}
